
import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  attribute?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
};

const initialState: {
  theme: Theme;
  setTheme: (theme: Theme) => void;
} = {
  theme: "system",
  setTheme: () => null,
};

const ThemeProviderContext = createContext(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  attribute = "data-theme",
  enableSystem = true,
  disableTransitionOnChange = false,
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove old data-theme attribute
    root.removeAttribute(attribute);

    // Set dark class for Tailwind dark mode
    if (theme === "system" && enableSystem) {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      
      root.classList.toggle("dark", systemTheme === "dark");
      root.setAttribute(attribute, systemTheme);
    } else {
      root.classList.toggle("dark", theme === "dark");
      root.setAttribute(attribute, theme);
    }

    // Disable transitions if requested
    if (disableTransitionOnChange) {
      const classToRemove = "transition-colors";
      document.body.classList.remove(classToRemove);
      
      // Re-enable transitions
      setTimeout(() => {
        document.body.classList.add(classToRemove);
      }, 100);
    }
    
    // Save theme to localStorage
    localStorage.setItem(storageKey, theme);
  }, [theme, attribute, enableSystem, disableTransitionOnChange, storageKey]);

  // Listen for system theme changes
  useEffect(() => {
    if (!enableSystem) return;
    
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = () => {
      if (theme === "system") {
        const root = window.document.documentElement;
        const systemTheme = mediaQuery.matches ? "dark" : "light";
        
        root.classList.toggle("dark", systemTheme === "dark");
        root.setAttribute(attribute, systemTheme);
      }
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, attribute, enableSystem]);

  return (
    <ThemeProviderContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  
  return context;
};
